"use client";

import { useCallback, useEffect, useState } from "react";
import { useRouter } from "next/navigation";

import { CustomLink } from "@/components/CustomLink";
import { removeExtraSpacesFromClassNames } from "@/utils/removeExtraSpacesFromClassNames";
import { optimizeURL } from "@/utils/url";
import styles from "./SeasonToggle.module.scss";

export type Season = "winter" | "summer";

export interface SeasonToggleOption {
  label: string;
  value: Season;
  href: string;
}

interface SeasonToggleProps {
  options: [SeasonToggleOption, SeasonToggleOption];
  activeValue: Season;
  orderValue: Season;
  ariaLabel: string;
}

const getPrefetchHref = (href: string) => {
  const optimizedHref = optimizeURL(href);

  if (!optimizedHref || !optimizedHref.startsWith("/")) {
    return null;
  }

  return optimizedHref;
};

export const SeasonToggle = ({
  options,
  activeValue,
  orderValue,
  ariaLabel,
}: SeasonToggleProps) => {
  const router = useRouter();
  const [selectedValue, setSelectedValue] = useState<Season>(activeValue);

  useEffect(() => {
    setSelectedValue(activeValue);
  }, [activeValue]);

  const orderedOptions = [...options].sort((optionA, optionB) => {
    if (optionA.value === orderValue) {
      return -1;
    }

    if (optionB.value === orderValue) {
      return 1;
    }

    return 0;
  });

  const prefetchOption = useCallback(
    (option: SeasonToggleOption) => {
      if (option.value === activeValue) {
        return;
      }

      const prefetchHref = getPrefetchHref(option.href);

      if (prefetchHref) {
        router.prefetch(prefetchHref);
      }
    },
    [activeValue, router],
  );

  useEffect(() => {
    options.forEach(prefetchOption);
  }, [options, prefetchOption]);

  return (
    <nav aria-label={ariaLabel} className={styles["toggle-container"]}>
      {orderedOptions.map((option) => {
        const isActive = option.value === selectedValue;
        return (
          <CustomLink
            key={option.value}
            href={option.href}
            target="_self"
            aria-current={isActive ? "page" : undefined}
            onClick={(event) => {
              if (option.value === activeValue) {
                event.preventDefault();
                return;
              }
              setSelectedValue(option.value);
            }}
            onFocus={() => prefetchOption(option)}
            onPointerEnter={() => prefetchOption(option)}
            className={removeExtraSpacesFromClassNames(
              `${styles["toggle-option"]} ${styles[`is-${option.value}`]} ${
                isActive ? styles["is-active"] : ""
              }`,
            )}
          >
            <span className={styles["toggle-label"]}>{option.label}</span>
          </CustomLink>
        );
      })}
    </nav>
  );
};
